import React, { useContext, useEffect, useRef } from 'react'
import { AppContext } from '@state/context'
import { createRoot } from 'react-dom/client'
import { request } from 'graphql-request'
import useSWR, { SWRConfig } from 'swr'
import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'
import { AppProvider } from '@state/context'

import Layout from '@views/Layout'

import SingleBook from '@templates/SingleBook'
import SingleSection from '@templates/SingleSection'
import MyTeachersRoom from '@templates/MyTeachersRoom'
import SingleSchoolClass from '@templates/SingleSchoolClass'
import Help from '@templates/Help'
import Profile from '@templates/Profile'
import FourOhFour from '@templates/FourOhFour'

import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import MyBookcase from '@templates/MyBookcase'
import Library from '@templates/Library'
import {
  PANELS,
  USER_ROLES,
  CUSTOMER_ACTIONS,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import StudentBooks from '@templates/StudentBooks'
import { useScrollToTop } from '@hooks/useScrollToTop'
import Home from '@templates/Home'
import { useOnNavigation } from '@hooks/useOnNavigation'
import {
  onNavigation,
  setCurrentActiveExam,
  setCurrentActiveExamAttemptId,
  setCurrentSessionLatestFinishedExam,
  setSelectedStudent,
} from '@state/actions/appActions'
import { useCustomerAction } from '@hooks/useCustomerAction'

import PublicSingleBook from '@templates/Public/SingleBook'
import PublicSingleSection from '@templates/Public/SingleSection'
import { Current_User } from '@graphql/queries'
import { closePanel, openPanel } from '@state/actions/panelsActions'
import SingleSchoolGroup from '@templates/SingleSchoolGroup'
import SingleDemoSchoolClass from '@templates/SingleDemoSchoolClass'
import SingleDemoSchoolGroup from '@templates/SingleDemoSchoolGroup'
import { trackPageView } from '@helpers/matomo'
import { preventPublicRequest } from '@helpers/api'
import FourOhOne from '@templates/FourOhOne'

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://5b7385f2b50843e882718c8ac047696f@o245925.ingest.sentry.io/5862578',
    integrations: [new Integrations.BrowserTracing()],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.2,
  })
}

const fetcher = async (query, id) => {
  const variables = typeof id === 'object' ? id : id ? { id: id } : {}

  return request('/graphql', query, variables)
}

const App = () => {
  const userIsGuest = !window.user
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { userRole },
  } = state
  const location = useLocation()
  const { trackCustomerAction } = useCustomerAction()
  const { data } = useSWR(preventPublicRequest(Current_User), fetcher)

  useEffect(() => {
    if (userRole !== 'student' && data && !data?.currentUser?.acceptedEula) {
      dispatch(openPanel(PANELS.USER_AGREEMENT))
    }

    if (data && data?.currentUser?.acceptedEula) {
      dispatch(closePanel(PANELS.USER_AGREEMENT))
    }
  }, [data])

  useEffect(() => {
    dispatch(onNavigation(location))
    trackCustomerAction(CUSTOMER_ACTIONS.DAILY_VISIT)
  }, [])

  useOnNavigation((location, lastPathname) => {
    dispatch(onNavigation(location))
    trackCustomerAction(CUSTOMER_ACTIONS.DAILY_VISIT)

    trackPageView(window.location.hash, lastPathname, userRole)

    // Clear possible current active exam and attempt
    dispatch(setCurrentActiveExam(null))
    dispatch(setCurrentActiveExamAttemptId(null))
    dispatch(setCurrentSessionLatestFinishedExam(null))

    // Clear selected student
    dispatch(setSelectedStudent(null))
  })

  // Track visit to library
  useEffect(() => {
    if (data && data.currentUser) {
      switch (window.location.hash) {
        case '#/bibliotek/bocker':
        case '#/bibliotek/avsnitt':
          if (!data.currentUser.hasVisitedLibrary) {
            trackCustomerAction(CUSTOMER_ACTIONS.VISITED_LIBRARY)
          }
          break
        case '#/hjalp':
          if (!data.currentUser.hasVisitedHelp) {
            trackCustomerAction(CUSTOMER_ACTIONS.VISITED_HELP)
          }
          break
        default:
          break
      }
    }
  }, [data, window.location.hash])

  useScrollToTop()

  return (
    // @TODO - check if we need refreshInterval like below
    <SWRConfig
      value={{
        revalidateOnFocus: false,
        // refreshInterval: 3000, // Disabled for now, probably not needed
        fetcher: fetcher,
        // onError: (error) => {
        //   console.log('🚀 ~ file: App.js ~ line 89 ~ error.message', error.message)
        //   console.log('🚀 ~ file: App.js ~ line 92 ~ error.response.status', error.response.status)

        //   return error
        // },
        use: [
          (useSWRNext) => {
            return (key, fetcher, config) => {
              const { data, error, isValidating, mutate } = useSWRNext(
                key,
                fetcher,
                config
              )

              if (
                error?.response?.errors?.some(
                  (e) => e.message === 'Unauthenticated.'
                )
              ) {
                window.user = undefined
                window.location.replace('/login')

                return false
              }

              return { data, error, isValidating, mutate }
            }
          },
        ],
      }}
    >
      <Layout>
        <Routes>
          <Route path='/delad/bok/:shareLink' element={<PublicSingleBook />} />
          <Route
            path='/delad/avsnitt/:shareLink'
            element={<PublicSingleSection />}
          />

          {userIsGuest && (
            <>
              <Route path='/bok/:id' element={<PublicSingleBook />} />
              <Route path='/avsnitt/:id' element={<PublicSingleSection />} />
            </>
          )}

          {/* Routes available for admins and teachers */}
          {(userRole === USER_ROLES.ADMIN ||
            userRole === USER_ROLES.TEACHER) && (
            <>
              <Route exact path='/' element={<Home />} />
              <Route path='/bokhylla/*' element={<MyBookcase />} />
              <Route path='/bibliotek/*' element={<Library />} />
              <Route path='/avsnitt/:id' element={<SingleSection />} />
              <Route path='/lararrum' element={<MyTeachersRoom />} />
              <Route path='/klass/:id' element={<SingleSchoolClass />} />
              <Route path='/grupp/:id' element={<SingleSchoolGroup />} />
              <Route
                path='/klass/:resourceId/bok/:id'
                element={
                  <SingleBook resourceType={SCHOOL_RESOURCE_TYPES.CLASS} />
                }
              />
              <Route
                path='/grupp/:resourceId/bok/:id'
                element={
                  <SingleBook resourceType={SCHOOL_RESOURCE_TYPES.GROUP} />
                }
              />
              <Route
                path='/demoklass/:id'
                element={<SingleDemoSchoolClass />}
              />
              <Route
                path='/demogrupp/:id'
                element={<SingleDemoSchoolGroup />}
              />
              <Route path='/hjalp' element={<Help />} />
              <Route path='/profil' element={<Profile />} />
              <Route path='/bok/:id' element={<SingleBook />} />
            </>
          )}

          {/* Routes available for student */}
          {userRole === USER_ROLES.STUDENT && (
            <>
              <Route
                exact
                path='/'
                element={<Navigate to='/bokhylla' replace />}
              />
              <Route path='/bokhylla' element={<StudentBooks />} />
              <Route path='/bok/:id' element={<SingleBook />} />
            </>
          )}

          <Route path='/401' element={<FourOhOne />} />
          <Route path='*' element={<FourOhFour />} />
        </Routes>
      </Layout>
    </SWRConfig>
  )
}

export default App

if (document.getElementById('app')) {
  createRoot(document.getElementById('app')).render(
    // <React.StrictMode>
    <AppProvider>
      <HashRouter>
        <App />
      </HashRouter>
    </AppProvider>
    // </React.StrictMode>
  )
}
